/**
 * The "nothing here yet" panel shown by lists, tables and dashboards.
 *
 * The icon sits in a muted circle so the block reads as an intentional state
 * rather than a failed render, and the optional `action` is where the primary
 * "create the first one" button goes.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { className, icon: Icon, title, description, action, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
      {...props}
    >
      {Icon ? (
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-sunken text-content-subtle ring-8 ring-surface-sunken/40"
          aria-hidden="true"
        >
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
      ) : null}

      <div className="max-w-md space-y-1">
        <p className="text-sm font-semibold text-content">{title}</p>
        {description ? <p className="text-sm text-content-muted">{description}</p> : null}
      </div>

      {action ? <div className="mt-1 flex items-center gap-2">{action}</div> : null}
    </div>
  );
});
